// data.ts - the locked dx4 Observe teaching hand, street by street. Transcribed 1:1 from the approved
// mockup (docs/ui-explorations/local-mode-read-first-v2.html, the STREETS array + the river recap).
//
// This is authored teaching content: the pro's specific stream-of-consciousness for ONE hand (A-J
// suited in the Cutoff vs a Vacuum who leads into hero). Authored teaching hands are sanctioned by
// PRODUCT_NORTH_STAR §5 - the same sanction under which engine/bridge.ts authors the felt cards. The
// narration is modeled as @postflop/coach's HandNarration shape in narration.ts; the beats here carry a
// token layer so every poker TERM stays tap-to-learn (a bare-string NarrationBlock can't).

/** One run of narration text. A plain string is prose; `{term}` is a tap-to-learn trigger; `{num}` is
 *  an emphasized money/count figure (rendered <b class="num">); `{archetype}` (CX9) is a player TYPE name
 *  rendered as a tap-to-learn <ArchetypeTerm> - tap it to learn what that type is + how to adjust. */
export type Token =
  | string
  | { term: string; text: string }
  | { num: string }
  | { archetype: import('@postflop/schema').ArchetypeId; text: string };

/** One narration beat - a numbered step in the pro's read. `decision` beats are the mint-highlighted
 *  "here is what I do" steps and carry a `tag` label. */
export interface ObserveBeat {
  tokens: Token[];
  decision?: boolean;
  tag?: string;
}

/** One of the five hand families the pro weights the villain into. `on` (0..3) drives the fill track;
 *  `weight` is the plain-word label (none / rare / some / a lot). */
export interface HandFamily {
  name: string;
  detail: string;
  on: 0 | 1 | 2 | 3;
  weight: string;
}

/** Everything the Observe view renders for one street. */
export interface ObserveStreetData {
  id: 'preflop' | 'flop' | 'turn' | 'river';
  kicker: string;
  h2: string;
  lede: string;
  /** evidence-ladder level lit this street (0 = none yet). */
  level: 0 | 1 | 2 | 3;
  bucketTitle: string;
  bucketSub: string;
  /** null preflop (no board yet, no weights). */
  families: HandFamily[] | null;
  beats: ObserveBeat[];
}

const fam = (name: string, detail: string, on: 0 | 1 | 2 | 3, weight: string): HandFamily => ({
  name,
  detail,
  on,
  weight,
});

export const OBSERVE_STREETS: ObserveStreetData[] = [
  {
    id: 'preflop',
    level: 0,
    kicker: 'Read the room first',
    h2: 'Before the cards, I read the room',
    lede: 'No decision to make. Follow how a winning reg builds the read, one street at a time.',
    bucketTitle: 'No board yet',
    bucketSub: 'First read the seat and the situation, then the cards.',
    families: null,
    beats: [
      {
        tokens: [
          'First the situation, not my cards. I am in the ',
          { term: 'position', text: 'Cutoff' },
          ', one of the last to act, so after the flop I almost always act last. The player on my right just ',
          { term: 'limp', text: 'limped' },
          ' in for the minimum.',
        ],
      },
      {
        tokens: [
          'I know this seat. He is a ',
          { term: 'vacuum', text: 'Vacuum' },
          ': he calls far too much and almost never raises. There is a second Vacuum across the table and a couple of seats I have not read yet.',
        ],
      },
      {
        tokens: [
          'So the room is loose and passive, and nobody here punishes a raise. Now my hand: A of spades, J of spades. Strong enough to raise, and raising lets me ',
          { term: 'isolate', text: 'isolate' },
          ' the limper and play the pot in position.',
        ],
      },
      {
        decision: true,
        tag: 'The decision',
        tokens: [
          'I raise to ',
          { num: '$18' },
          ', bigger than a TV raise, because small raises here get five callers. The blinds fold, the Vacuum calls. Heads up to the flop, and I act last the rest of the way.',
        ],
      },
    ],
  },
  {
    id: 'flop',
    level: 1,
    kicker: 'The flop',
    h2: 'One odd bet, and what it means',
    lede: 'J 8 4, dry. I flop a strong hand, then the Vacuum does something he never does.',
    bucketTitle: 'How he weights the hands',
    bucketSub: 'Mostly medium, because a Vacuum bets pairs far more than air.',
    families: [
      fam('Nuts', 'sets, two pair', 1, 'rare'),
      fam('Strong made', 'a better jack', 1, 'some'),
      fam('Medium pairs', '8x, weak jack', 3, 'a lot'),
      fam('Draws', 'gutshot, overs', 2, 'some'),
      fam('Air, bluff', 'pure nothing', 1, 'rare'),
    ],
    beats: [
      {
        tokens: [
          'The flop is J of diamonds, 8 of clubs, 4 of spades. I hit ',
          { term: 'tpk', text: 'top pair, top kicker' },
          '. On this dry board very little already beats me.',
        ],
      },
      {
        tokens: [
          'Then something odd: the Vacuum leads out for ',
          { num: '$30' },
          ' into ',
          { num: '$43' },
          '. He has not bet first all night. That break from his pattern is the only new information in the hand.',
        ],
      },
      {
        tokens: [
          'On the ',
          { term: 'ladder', text: 'evidence ladder' },
          " that is Level 1: one unusual action. It is a question, not a conclusion. I do not fold a strong hand to a Vacuum's first bet of the night.",
        ],
      },
      {
        tokens: [
          'His hands are mostly medium pairs and weak jacks. A Vacuum turns a pair into a bet far more often than he turns nothing into a bluff, so I weight him toward the middle, not the nuts and not air.',
        ],
      },
      {
        decision: true,
        tag: 'The decision',
        tokens: [
          'I just ',
          { term: 'flatcall', text: 'call' },
          '. Raising folds out every worse hand that pays me and keeps only what beats me. Calling keeps his whole ',
          { term: 'range', text: 'range' },
          ' in and takes the cheap ',
          { term: 'thin', text: 'value' },
          '.',
        ],
      },
    ],
  },
  {
    id: 'turn',
    level: 2,
    kicker: 'The turn',
    h2: 'He fires again. That changes the read',
    lede: 'The turn is where local hands become readable. A second bet is a second piece of evidence.',
    bucketTitle: 'The weights shift up',
    bucketSub: 'Two bets from a Vacuum means he likes it. More value now, fewer weak pairs.',
    families: [
      fam('Nuts', 'sets, two pair', 2, 'some'),
      fam('Strong made', 'a better jack', 3, 'a lot'),
      fam('Medium pairs', '8x, weak jack', 2, 'some'),
      fam('Draws', 'gutshot, overs', 1, 'rare'),
      fam('Air, bluff', 'pure nothing', 1, 'rare'),
    ],
    beats: [
      {
        tokens: [
          'The turn is the 2 of hearts, a total ',
          { term: 'blank', text: 'blank' },
          '. It changes nothing about who is ahead on the board.',
        ],
      },
      {
        tokens: [
          'But the Vacuum bets again, ',
          { num: '$40' },
          ' into ',
          { num: '$103' },
          '. That is a ',
          { term: 'barrel', text: 'second barrel' },
          ' from a man who never bets. The same aggression twice is Level 2 on the ladder: a pattern, not a one-off.',
        ],
      },
      {
        tokens: [
          'A Vacuum firing twice is telling me he likes his hand. I shift the read upward: more of his range is now two pair and sets, fewer weak pairs. My top pair is no longer clearly ahead.',
        ],
      },
      {
        decision: true,
        tag: 'The decision',
        tokens: [
          'I call once more, because he still shows up with enough worse jacks and stubborn pairs to make one call right. But I decide my river plan now: if a blank river brings a third big bet, I am done with this hand.',
        ],
      },
    ],
  },
  {
    id: 'river',
    level: 3,
    kicker: 'The river',
    h2: 'The read beats the hand',
    lede: 'Value and discipline. A big bet from a passive player is almost always what it looks like.',
    bucketTitle: 'A value shape',
    bucketSub: 'Three barrels on a board where draws bricked. Almost no worse hand bets like this.',
    families: [
      fam('Nuts', 'sets, two pair', 3, 'a lot'),
      fam('Strong made', 'a better jack', 3, 'a lot'),
      fam('Medium pairs', '8x, weak jack', 1, 'rare'),
      fam('Draws', 'gutshot, overs', 0, 'none'),
      fam('Air, bluff', 'pure nothing', 1, 'rare'),
    ],
    beats: [
      {
        tokens: ['The river is the 7 of diamonds. Another blank. Every obvious draw missed.'],
      },
      {
        tokens: [
          'The Vacuum moves all in for ',
          { num: '$120' },
          ' into ',
          { num: '$183' },
          '. A ',
          { term: 'barrel', text: 'third barrel' },
          ', all in, from a lifelong caller, on a board where nothing got there. That is Level 3: repeated aggression, about to be shown down.',
        ],
      },
      {
        tokens: [
          'I ask the only question that matters: what worse hand bets like this? Against a Vacuum, almost none. He is not turning a made hand into a triple-barrel bluff. My top pair, top kicker is now a clear ',
          { term: 'bluffcatch', text: 'bluff-catcher' },
          ' that mostly loses.',
        ],
      },
      {
        decision: true,
        tag: 'The decision',
        tokens: [
          'So I fold. Folding a pretty hand is cheaper than paying to find out he finally has it. The read beat the hand, and I trust the read.',
        ],
      },
    ],
  },
];

/** The river-only takeaway recap (mockup .recap). */
export interface RecapRow {
  street: string;
  text: string;
}
export const OBSERVE_RECAP = {
  title: 'The read moved. The right action moved with it.',
  rows: [
    { street: 'Flop', text: 'One odd bet. A question, Level 1. Stay wide, call for cheap value.' },
    { street: 'Turn', text: 'He fires again. A pattern, Level 2. Call once more, but plan the river.' },
    { street: 'River', text: 'A third barrel from a man who never bluffs. Level 3. Fold the pretty hand.' },
  ] as RecapRow[],
  close:
    'Same top pair the whole way. Three different correct actions, because the read kept changing. That is the skill: not the cards, the reading.',
};
